import { supabase } from '@/lib/supabase';
import { logger } from '@/utils/logger';

export type NotificationRow = {
  user_id: string;
  title: string;
  message: string;
  type: string;
  /** Must include RLS keys when volunteer notifies org: application_id | membership_id | activity_id */
  data?: Record<string, unknown> | null;
  read?: boolean;
};

/**
 * In-app notification insert (fire-and-forget). Logs PostgREST/RLS errors.
 *
 * Org-bound types (volunteer → organization) require in `data`:
 * - event_application_pending: application_id, event_id, …
 * - organization_membership_pending: membership_id, organization_id, …
 * - task_submission_pending: activity_id, task_id, event_id, …
 * - hours_request_pending: activity_id, event_id, …
 *
 * Chat notifications (event_message / department_message / direct_message) do NOT come
 * through here. They are fanned out server-side by notify_chat_message(), a trigger on
 * messages_new -- RLS gives a volunteer no way to write a notifications row for another
 * volunteer, so a group chat could never have been notified from the client. See
 * supabase/migrations/20260909120100_chat_message_notifications.sql.
 */
export function insertNotification(row: NotificationRow): void {
  // Do not chain .select() here: volunteers insert rows for org recipients, but SELECT
  // RLS only allows user_id = auth.uid(), so RETURNING would fail and can block the insert.
  // Push notification is sent automatically via a DB trigger on this table.
  void supabase
    .from('notifications')
    .insert({
      user_id: row.user_id,
      title: row.title,
      message: row.message,
      type: row.type,
      data: row.data ?? {},
      read: row.read ?? false,
    })
    .then(({ error }) => {
      if (error) logger.error('notifications.insert failed', { user_id: row.user_id, type: row.type, error });
    });
}
